import { useEffect, useState, useRef } from 'react';
import { Button, Divider, RadioButton, RadioButtonProps, Select, Thumbnail, TextField } from '@shopify/polaris';
import { useAppBridge } from '@shopify/app-bridge-react';
import useMainStore from '../store/useMainStore';
import { useShallow } from 'zustand/shallow';
import { useNavigate } from 'react-router-dom';
import {
    CompanyLocationsResponse,
    FileCreateResponse,
    LocationsListResponse,
    OrdersListResponse,
    StagedUploadsCreateResponse,
} from '../types';
import dayjs from 'dayjs';
import {
    DEFAULT_DATE_FORMAT,
    MUTATION_METAFIELD_SET,
    MUTATION_FILE_CREATE,
    MUTATION_STAGED_UPLOADS_CREATE,
    QUERY_GET_COMPANY_LOCATIONS,
    QUERY_GET_LOCATIONS,
    QUERY_GET_ORDERS_LIST,
} from '../constants';
import { XIcon, CheckCircleIcon } from '@shopify/polaris-icons';
import Loading from './Loading';
import { FILE_CREATE_CONTENT_TYPE, STAGED_UPLOAD_RESOURCES } from '../constants/api.constants';
import axios from 'axios';
import { config } from '../config/app.config';

const METAFIELD_OPTIONS = {
    VOUCHER: 'voucher_image',
    BADGE: 'badge_image',
    COMPANY_ID: 'company_id',
    PAYROLL_DEDUCTION_FORM: 'payroll_deduction_form',
    DRIVER_LICENSE: 'driver_license',
    SIGNATURE: 'signature',
    CUSTOM: 'custom',
} as const;

const extractNumericId = (id: string): string => {
    if (id.startsWith('gid://')) {
        return id.split('/').pop() ?? id;
    }
    return id;
};

const StoresList = () => {
    const app = useAppBridge();
    const setToast = useMainStore((state) => state.setToast);

    const [loading, setLoading] = useState({
        loadData: false,
        submit: false,
    });
    const [orderId, setOrderId] = useState<string | null>(null);
    const [orders, setOrders] = useState<OrdersListResponse['data']['orders']['edges']>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [metafieldType, setMetafieldType] = useState<string>(METAFIELD_OPTIONS.VOUCHER);
    const [customMetafieldKey, setCustomMetafieldKey] = useState('');
    const [locationOptions, setLocationOptions] = useState<{ label: string; value: string }[]>([]);
    const [selectedLocationId, setSelectedLocationId] = useState('');
    const [companyLocationNames, setCompanyLocationNames] = useState<Record<string, string>>({});

    const hasMountedRef = useRef(false);
    // Ref keeps location ID accessible in the debounced search effect without stale closure
    const locationIdRef = useRef('');

    const router = useNavigate();
    const {
        currentImage,
        imageSource,
        setCurrentImage,
        lastSelectedOrderId,
        setLastSelectedOrderId,
        lastOrderSearchQuery,
        setLastOrderSearchQuery,
    } = useMainStore(
        useShallow((state) => ({
            currentImage: state.currentImage,
            imageSource: state.imageSource,
            setCurrentImage: state.setCurrentImage,
            lastSelectedOrderId: state.lastSelectedOrderId,
            setLastSelectedOrderId: state.setLastSelectedOrderId,
            lastOrderSearchQuery: state.lastOrderSearchQuery,
            setLastOrderSearchQuery: state.setLastOrderSearchQuery,
        })),
    );

    const handleOrderChange: NonNullable<RadioButtonProps['onChange']> = (_, newValue) => {
        setOrderId(newValue);
        setLastSelectedOrderId(newValue);
    };

    const handleMetafieldTypeChange: NonNullable<RadioButtonProps['onChange']> = (_, newValue) => {
        setMetafieldType(newValue);
        if (newValue !== METAFIELD_OPTIONS.CUSTOM) {
            setCustomMetafieldKey('');
        }
    };

    const getMetafieldKey = (): string => {
        if (metafieldType === METAFIELD_OPTIONS.CUSTOM && customMetafieldKey) {
            return customMetafieldKey
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, '');
        }
        return metafieldType;
    };

    const onCancel = () => {
        setCurrentImage(null);
        router('/');
    };

    const getOrdersList = async (query: string = '', locationId: string = '') => {
        setLoading((state) => ({ ...state, loadData: true }));
        const parts: string[] = [];

        if (query) {
            parts.push(query);
        }

        if (locationId) {
            parts.push(`location_id:${locationId}`);
        }

        const combinedQuery = parts.join(' AND ');

        try {
            const response = await axios.post<OrdersListResponse>(
                config.shopifyDirectUrl,
                {
                    query: QUERY_GET_ORDERS_LIST,
                    variables: {
                        first: 5,
                        ...(combinedQuery && { query: combinedQuery }),
                    },
                },
                {
                    adapter: 'fetch',
                },
            );
            const edges = response?.data?.data?.orders?.edges || [];
            setOrders(edges);

            const locationGids = [
                ...new Set(
                    edges
                        .map(
                            ({ node }) =>
                                node.customAttributes?.find((attr) => attr.key === 'Company Location ID')?.value,
                        )
                        .filter((id): id is string => !!id)
                        .map((id) => `gid://shopify/CompanyLocation/${id}`),
                ),
            ];

            if (locationGids.length > 0) {
                try {
                    const locResponse = await axios.post<CompanyLocationsResponse>(
                        config.shopifyDirectUrl,
                        { query: QUERY_GET_COMPANY_LOCATIONS, variables: { ids: locationGids } },
                        { adapter: 'fetch' },
                    );
                    const names: Record<string, string> = {};
                    for (const node of locResponse?.data?.data?.nodes ?? []) {
                        if (node) {
                            names[extractNumericId(node.id)] = node.name;
                        }
                    }
                    setCompanyLocationNames(names);
                } catch (_error) {
                    // company location names unavailable
                }
            } else {
                setCompanyLocationNames({});
            }
        } catch (error) {
            setToast({
                content: (error as Error).message,
                icon: XIcon,
                error: true,
            });
        } finally {
            setLoading((state) => ({ ...state, loadData: false }));
        }
    };

    const fetchLocations = async (): Promise<string> => {
        let currentLocationId = '';

        if (app.environment?.pos) {
            try {
                const posLocation = await app.pos.location();
                if (posLocation?.id != null) {
                    currentLocationId = String(posLocation.id);
                }
            } catch (_error) {
                // pos.location() failed
            }
        }

        try {
            const response = await axios.post<LocationsListResponse>(
                config.shopifyDirectUrl,
                { query: QUERY_GET_LOCATIONS },
                { adapter: 'fetch' },
            );
            const edges = response?.data?.data?.locations?.edges ?? [];
            const locationIds = edges.map(({ node }) => extractNumericId(node.id));

            setLocationOptions([
                { label: 'All locations', value: '' },
                ...edges.map(({ node }) => ({
                    label: node.name,
                    value: extractNumericId(node.id),
                })),
            ]);

            return locationIds.includes(currentLocationId) ? currentLocationId : '';
        } catch (error) {
            setToast({
                content: (error as Error).message,
                icon: XIcon,
                error: true,
            });
            return '';
        }
    };

    const handleLocationChange = (newLocationId: string) => {
        setSelectedLocationId(newLocationId);
        locationIdRef.current = newLocationId;
        getOrdersList(searchQuery, newLocationId);
    };

    // Initial load effect
    useEffect(() => {
        if (!currentImage) {
            router('/');
            return;
        }

        if (imageSource === 'signature') {
            setMetafieldType(METAFIELD_OPTIONS.SIGNATURE);
        }

        if (lastOrderSearchQuery) {
            setSearchQuery(lastOrderSearchQuery);
        }

        if (lastSelectedOrderId) {
            setOrderId(lastSelectedOrderId);
        }

        fetchLocations().then((locationId) => {
            setSelectedLocationId(locationId);
            locationIdRef.current = locationId;
            getOrdersList(lastOrderSearchQuery, locationId);
            hasMountedRef.current = true;
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only once on mount

    // Debounced search effect
    useEffect(() => {
        if (!hasMountedRef.current) {
            return;
        }

        const timerId = setTimeout(() => {
            getOrdersList(searchQuery, locationIdRef.current);
            setLastOrderSearchQuery(searchQuery);
        }, 500);

        return () => {
            clearTimeout(timerId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const onSubmit = async () => {
        if (!currentImage) return;

        const finalMetafieldKey = getMetafieldKey();
        if (!finalMetafieldKey) {
            setToast({
                content: 'Please provide a valid metafield key',
                icon: XIcon,
                error: true,
            });
            return;
        }

        try {
            setLoading((state) => ({ ...state, submit: true }));

            // Step 1: Create staged upload
            const stagedRequest = {
                query: MUTATION_STAGED_UPLOADS_CREATE,
                variables: {
                    input: [
                        {
                            filename: currentImage.name,
                            mimeType: currentImage.type,
                            httpMethod: 'POST',
                            resource: STAGED_UPLOAD_RESOURCES.productImage,
                        },
                    ],
                },
            };

            const stagedResponse = await axios.post<StagedUploadsCreateResponse>(
                config.shopifyDirectUrl,
                stagedRequest,
                {
                    adapter: 'fetch',
                },
            );

            const stagedTarget = stagedResponse?.data?.data?.stagedUploadsCreate?.stagedTargets?.[0];

            // Step 2: Upload file to staged target
            const formData = new FormData();
            stagedTarget.parameters.forEach((param) => {
                formData.append(param.name, param.value);
            });
            formData.append('file', currentImage);

            const uploadResponse = await axios.post(stagedTarget.url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                responseType: 'text',
            });

            const locationMatch = uploadResponse.data.match(/<Location>(.*?)<\/Location>/);
            const url = locationMatch ? locationMatch[1] : null;

            // Step 3: Create file in Shopify
            const fileCreateRequest = {
                query: MUTATION_FILE_CREATE,
                variables: {
                    files: [
                        {
                            alt: currentImage.name,
                            contentType: FILE_CREATE_CONTENT_TYPE.image,
                            originalSource: url,
                        },
                    ],
                },
            };

            const fileCreateResponse = await axios.post<FileCreateResponse>(
                config.shopifyDirectUrl,
                fileCreateRequest,
                {
                    adapter: 'fetch',
                },
            );

            const fileId = fileCreateResponse?.data?.data?.fileCreate?.files?.[0]?.id;

            // Step 4: Set metafield with file reference
            const metafieldSetRequest = {
                query: MUTATION_METAFIELD_SET,
                variables: {
                    metafields: [
                        {
                            ownerId: orderId,
                            namespace: 'custom',
                            key: finalMetafieldKey,
                            type: 'file_reference',
                            value: fileId,
                        },
                    ],
                },
            };

            const metafieldSetResponse = await axios.post(config.shopifyDirectUrl, metafieldSetRequest, {
                adapter: 'fetch',
            });

            if (metafieldSetResponse?.status === 200) {
                setToast({
                    content: 'Saved',
                    icon: CheckCircleIcon,
                    tone: 'magic',
                });
                setTimeout(() => {
                    setCurrentImage(null);
                    router('/');
                }, 750);
            } else {
                setToast({
                    content: 'Something went wrong!',
                    icon: XIcon,
                    error: true,
                });
            }
        } catch (error) {
            setToast({
                content: (error as Error).message,
                icon: XIcon,
                error: true,
            });
        } finally {
            setLoading((state) => ({ ...state, submit: false }));
        }
    };

    // Show full-page loading only on initial load
    if (loading.loadData && !hasMountedRef.current) {
        return <Loading />;
    }

    return (
        <div>
            {currentImage && (
                <Thumbnail source={URL.createObjectURL(currentImage)} size="large" alt={currentImage.name} />
            )}

            <div className="my-6">
                <Divider />
            </div>

            {/* Metafield Type Selection */}
            {imageSource === 'signature' ? (
                <div className="mb-6">
                    <p className="font-semibold mb-3">Metafield Type:</p>
                    <p className="text-sm text-gray-600">
                        This signature will be automatically saved as <strong>Signature</strong>
                    </p>
                </div>
            ) : (
                <div className="mb-6">
                    <p className="font-semibold mb-3">Select Metafield Type:</p>
                    <div className="flex flex-col gap-3">
                        <RadioButton
                            name="metafieldType"
                            id={METAFIELD_OPTIONS.VOUCHER}
                            label="Voucher Image"
                            onChange={handleMetafieldTypeChange}
                            checked={metafieldType === METAFIELD_OPTIONS.VOUCHER}
                            disabled={loading.submit}
                        />
                        <RadioButton
                            name="metafieldType"
                            id={METAFIELD_OPTIONS.BADGE}
                            label="Badge Image"
                            onChange={handleMetafieldTypeChange}
                            checked={metafieldType === METAFIELD_OPTIONS.BADGE}
                            disabled={loading.submit}
                        />
                        <RadioButton
                            name="metafieldType"
                            id={METAFIELD_OPTIONS.COMPANY_ID}
                            label="Company ID"
                            onChange={handleMetafieldTypeChange}
                            checked={metafieldType === METAFIELD_OPTIONS.COMPANY_ID}
                            disabled={loading.submit}
                        />
                        <RadioButton
                            name="metafieldType"
                            id={METAFIELD_OPTIONS.PAYROLL_DEDUCTION_FORM}
                            label="Payroll Deduction Form"
                            onChange={handleMetafieldTypeChange}
                            checked={metafieldType === METAFIELD_OPTIONS.PAYROLL_DEDUCTION_FORM}
                            disabled={loading.submit}
                        />
                        <RadioButton
                            name="metafieldType"
                            id={METAFIELD_OPTIONS.DRIVER_LICENSE}
                            label="Driver's License"
                            onChange={handleMetafieldTypeChange}
                            checked={metafieldType === METAFIELD_OPTIONS.DRIVER_LICENSE}
                            disabled={loading.submit}
                        />
                        <RadioButton
                            name="metafieldType"
                            id={METAFIELD_OPTIONS.CUSTOM}
                            label="Custom"
                            onChange={handleMetafieldTypeChange}
                            checked={metafieldType === METAFIELD_OPTIONS.CUSTOM}
                            disabled={loading.submit}
                        />
                    </div>

                    {metafieldType === METAFIELD_OPTIONS.CUSTOM && (
                        <div className="mt-3">
                            <TextField
                                label="Custom Metafield Key"
                                value={customMetafieldKey}
                                onChange={setCustomMetafieldKey}
                                placeholder="Enter custom key"
                                autoComplete="off"
                                disabled={loading.submit}
                                helpText="Will be converted to snake_case lowercase"
                            />
                        </div>
                    )}
                </div>
            )}

            <div className="my-6">
                <Divider />
            </div>

            {/* Location Filter */}
            {locationOptions.length > 0 && (
                <div className="mb-4">
                    <Select
                        label="Location"
                        options={locationOptions}
                        value={selectedLocationId}
                        onChange={handleLocationChange}
                        disabled={loading.submit}
                    />
                </div>
            )}

            {/* Search Field */}
            <div className="mb-4">
                <TextField
                    label="Search Orders"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search by order name..."
                    autoComplete="off"
                    clearButton
                    onClearButtonClick={() => setSearchQuery('')}
                    disabled={loading.submit}
                />
            </div>

            {/* Orders List */}
            {loading.loadData ? (
                <div className="flex justify-center py-8">
                    <Loading />
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {orders.length > 0 ? (
                        orders.map(({ node }) => {
                            const receiptNumber = node.sourceIdentifier
                                ? node.sourceIdentifier.split('-').slice(1).join('-')
                                : undefined;
                            const companyLocationId = node.customAttributes?.find(
                                (attr) => attr.key === 'Company Location ID',
                            )?.value;
                            const locationName = companyLocationId
                                ? companyLocationNames[companyLocationId]
                                : undefined;
                            const employeeName = node.customAttributes?.find(
                                (attr) => attr.key === 'Employee Name',
                            )?.value;
                            const employeeId = node.customAttributes?.find(
                                (attr) => attr.key === 'Employee ID/Badge #',
                            )?.value;
                            const lineItemsSummary = node.lineItems.edges
                                .map(({ node: item }) => `${item.title} x${item.quantity}`)
                                .join(', ');

                            return (
                                <RadioButton
                                    name="order"
                                    id={node?.id}
                                    key={node?.id}
                                    label={node?.name}
                                    onChange={handleOrderChange}
                                    checked={orderId === node?.id}
                                    disabled={loading.submit}
                                    helpText={
                                        <span className="flex flex-col gap-0.5">
                                            {receiptNumber && <span>Receipt #: {receiptNumber}</span>}
                                            {locationName && <span>Company/Dep: {locationName}</span>}
                                            {employeeName && <span>Employee: {employeeName}</span>}
                                            {employeeId && <span>Employee ID: {employeeId}</span>}
                                            {lineItemsSummary && <span>{lineItemsSummary}</span>}
                                            <span>Created: {dayjs(node.createdAt).format(DEFAULT_DATE_FORMAT)}</span>
                                        </span>
                                    }
                                />
                            );
                        })
                    ) : (
                        <p className="text-center py-4 text-gray-500">No orders found</p>
                    )}
                </div>
            )}

            <div className="sticky bottom-8 mt-12 flex justify-end items-center gap-4">
                <Button onClick={onCancel} disabled={loading.submit}>
                    Cancel
                </Button>
                <Button variant="primary" disabled={!orderId} onClick={onSubmit} loading={loading.submit}>
                    Save
                </Button>
            </div>
        </div>
    );
};

export default StoresList;
